import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IPracticeMigration,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IGetRecordResponse,
  type ISourceEntityHandler,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand, Patient, PatientForm } from '@principle-theorem/principle-core';
import {
  AnyFormLayout,
  CustomFormDataResolverConfigMap,
  IFormSchema,
  PatientFormType,
  type IBrand,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  FirestoreMigrate,
  getError,
  Timestamp,
  toTimestamp,
  type DocumentReference,
  type PrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';
import { PATIENT_RESOURCE_TYPE } from './patient';

export const PATIENT_MEDICAL_HISTORY_RESOURCE = 'patientMedicalHistory';

export const PATIENT_MEDICAL_HISTORY_DESTINATION_ENTITY =
  DestinationEntity.init({
    metadata: {
      key: PATIENT_MEDICAL_HISTORY_RESOURCE,
      label: 'Patient Medical Histories',
      description: '',
    },
  });

export interface IPatientMedicalHistoryDestinationRecord {
  patientRef: DocumentReference<IPatient>;
}

export interface IPatientMedicalHistoryJobData<
  Patient extends ISourceEntityTypes,
> {
  brand: WithRef<IBrand>;
  sourcePatient: IGetRecordResponse<Patient>;
  medicalConditions: ITranslationMap<object, string>[];
  allergies: ITranslationMap<object, string>[];
}

export interface IPatientMedicalHistoryFormSubmission {
  data: object;
  date: Timestamp;
  disableChanges: boolean;
  dataResolverConfig?: CustomFormDataResolverConfigMap;
  schema?: IFormSchema;
  layout?: AnyFormLayout[];
}

export abstract class BasePatientMedicalHistoryDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
  JobData extends IPatientMedicalHistoryJobData<PatientRecord>,
> extends BaseDestinationEntity<
  IPatientMedicalHistoryDestinationRecord,
  JobData,
  JobData
> {
  destinationEntity = PATIENT_MEDICAL_HISTORY_DESTINATION_ENTITY;
  abstract patientSourceEntity: ISourceEntityHandler<PatientRecord>;
  abstract buildFormSubmission(
    migration: WithRef<IPracticeMigration>,
    data: JobData
  ): Promise<IPatientMedicalHistoryFormSubmission | undefined>;

  get sourceCountComparison(): ISourceEntityHandler<PatientRecord> {
    return this.patientSourceEntity;
  }

  sourceCountDataAccessor(data: JobData): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: JobData): string {
    return data.sourcePatient.record.uid;
  }

  buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: JobData
  ): JobData | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    return data;
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: JobData
  ): JobData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: JobData,
    migrationData: JobData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      label: migrationData.sourcePatient.record.label,
      uid: migrationData.sourcePatient.record.uid,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<PatientRecord>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage: string
  ): IDestinationEntityRecord<IPatientMedicalHistoryDestinationRecord> {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage,
      failData: {
        patientRef: patient.id,
      },
    };
  }

  buildSuccessResponse(
    patientRef: DocumentReference<IPatient>,
    patient: Pick<
      IGetRecordResponse<PatientRecord>['record'],
      'label' | 'uid' | 'id'
    >
  ): IDestinationEntityRecord<IPatientMedicalHistoryDestinationRecord> {
    return {
      uid: patient.uid,
      label: patient.label,
      data: {
        patientRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.id,
      migratedAt: toTimestamp(),
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: JobData
  ): Promise<IDestinationEntityRecord> {
    try {
      const sourcePatientId = this.patientSourceEntity
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this.buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const formSubmission = await this.buildFormSubmission(migration, data);
      if (!formSubmission) {
        return this.buildSkippedResponse(data);
      }

      await this.addFormSubmissionToPatient(
        formSubmission,
        translationMap,
        data,
        patientRef
      );
      return this.buildSuccessResponse(patientRef, data.sourcePatient.record);
    } catch (error) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        `Error processing patient medical history: ${getError(error)}`
      );
    }
  }

  async addFormSubmissionToPatient(
    formSubmission: IPatientMedicalHistoryFormSubmission,
    translationMap: TranslationMapHandler,
    data: JobData,
    patientRef: DocumentReference<IPatient>
  ): Promise<void> {
    const existingMedicalHistoryRef = await translationMap.getDestination(
      data.sourcePatient.record.uid,
      PATIENT_MEDICAL_HISTORY_RESOURCE
    );

    const patientReffable = { ref: patientRef };
    const formConfigRef = Brand.medicalHistoryFormRef({
      ref: Patient.brandRef(patientReffable),
    });

    const patientForm = PatientForm.markConfirmed(
      PatientForm.init({
        form: formSubmission,
        formType: PatientFormType.CustomForm,
        template: { name: 'Medical History Form', ref: formConfigRef },
      }),
      formSubmission.date
    );

    const medicalHistoryRef = await FirestoreMigrate.upsertDoc(
      Patient.formCol(patientReffable),
      {
        ...patientForm,
        createdAt: formSubmission.date,
        updatedAt: formSubmission.date,
      },
      existingMedicalHistoryRef?.id,
      undefined,
      { omitUpdateTimestamp: true }
    );

    if (!existingMedicalHistoryRef) {
      await translationMap.upsert({
        sourceIdentifier: data.sourcePatient.record.uid,
        destinationIdentifier: medicalHistoryRef,
        resourceType: PATIENT_MEDICAL_HISTORY_RESOURCE,
      });
    }
  }
}
